import { useCurrentUser, type CurrentUser } from "@nabble/sdk";
import { createContext, type ReactNode, useContext, useEffect, useMemo, useRef } from "react";
import type { AuthState, AuthStep } from "./session";
import { useAuthState } from "./useAuthState";
import { useLogout } from "./useLogout";
import { useSessionLoss } from "./useSessionLoss";

/** The backend's idle timeout until GET /api/auth/state says otherwise. */
const DEFAULT_IDLE_MINUTES = 30;

interface AuthContextValue {
  state: AuthState | null;
  user: CurrentUser | null;
  loading: boolean;
  /** Backend idle timeout in minutes (policy), for the session keep-alive. */
  sessionIdleMinutes: number;
  /**
   * Set for a "Keep me signed in" session (ISO instant it ends): inactivity
   * doesn't end it, so there is no idle-expiry warning.
   */
  rememberedUntil: string | null;
  refreshState: () => Promise<AuthState | null>;
  /**
   * Updates the known auth step from a step endpoint's own response (every
   * `/api/auth/*` step call already returns `{ next }`), without another
   * round trip to `GET /api/auth/state`. `/api/auth/*` is rate-limited at
   * the edge, so the login/enrollment flow applies the step it already has
   * instead of re-fetching it.
   */
  applyNext: (next: AuthStep, rememberedUntil?: string | null) => void;
  refreshUser: () => Promise<void>;
  /** Explicit sign out: ends the server session and clears the local state. */
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const store = useAuthState();
  const { state, loading, refreshState, applyNext } = store;
  const { user, refresh: refreshUser } = useCurrentUser();
  const loggingOut = useRef(false);
  useSessionLoss(store, loggingOut);
  const logout = useLogout({ applyNext, refreshUser, loggingOut });

  // `useCurrentUser` only fetches /api/me once, on its own mount — which for
  // a fresh login happens before the user is actually authenticated (it's
  // mounted once at the app root). Re-fetch it whenever the auth step
  // reaches DONE so `user` (name, permissions) is populated right after
  // login, not only after a full page reload.
  useEffect(() => {
    if (state?.authenticated && state.next === "DONE") {
      void refreshUser();
    }
  }, [state?.authenticated, state?.next, refreshUser]);

  const value = useMemo<AuthContextValue>(
    () => ({
      state,
      user,
      loading,
      sessionIdleMinutes: state?.sessionIdleMinutes ?? DEFAULT_IDLE_MINUTES,
      rememberedUntil: state?.authenticated ? (state.rememberedUntil ?? null) : null,
      refreshState,
      applyNext,
      refreshUser,
      logout,
    }),
    [state, user, loading, refreshState, applyNext, refreshUser, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
